/**
 * ManualScreeningCard Component
 * 
 * Compact screening question card with edit/delete actions
 */

import React from 'react';
import { Card, Typography, Button, Tooltip, Popconfirm, Tag } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { ManualScreeningQuestionState } from '../utils/formTransformers';

interface ManualScreeningCardProps {
  question: ManualScreeningQuestionState;
  index: number;
  onEdit: (question: ManualScreeningQuestionState) => void;
  onDelete: (id: string | number) => void;
  isEditMode?: boolean; // Whether in edit job post mode
  readOnly?: boolean; // Whether the entire section is read-only (no edit/delete at all)
}

const ManualScreeningCard: React.FC<ManualScreeningCardProps> = ({
  question,
  onEdit,
  onDelete,
  isEditMode = false,
  readOnly = false,
}) => {
  // In read-only mode, nothing can be edited or deleted
  // In edit mode, only existing questions cannot be edited or deleted
  const canEditOrDelete = !readOnly && (!isEditMode || !question._isExisting);

  const handleDelete = () => {
    onDelete(question.id);
  };

  return (
    <Card
      className="manual-screening-card"
      style={{
        marginBottom: 6,
      }}
      bodyStyle={{ padding: '6px 10px' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Description - wrapped text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography.Text style={{ lineHeight: 1.5, wordWrap: 'break-word', whiteSpace: 'normal' }}>
            {question.description}
          </Typography.Text>
        </div>

        {/* Actions - only show for new questions in edit mode */}
        {canEditOrDelete ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <Tooltip title="Edit screening question">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEdit(question)}
                style={{ padding: '2px 6px', height: 24 }}
              />
            </Tooltip>

            <Popconfirm
              title="Delete screening question?"
              description="This action cannot be undone."
              onConfirm={handleDelete}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete screening question">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ padding: '2px 6px', height: 24 }}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        ) : (
          <Tooltip title="Existing questions cannot be modified when editing a job post">
            <Typography.Text type="secondary" style={{ fontSize: 'var(--ant-font-size-sm)' }}>
              Existing
            </Typography.Text>
          </Tooltip>
        )}
      </div>
    </Card>
  );
};

export default ManualScreeningCard;

